import { useEffect } from 'react';
import type { RefObject } from 'react';

type DismissOptions = {
  open: boolean;
  onDismiss: () => void;
  /** Elements that should NOT count as "outside" — the panel and its trigger. */
  refs: Array<RefObject<HTMLElement | null>>;
  /** Set false for modals, which handle Escape themselves alongside a focus trap. */
  closeOnEscape?: boolean;
};

/**
 * Closes a floating surface on Escape or a click outside it.
 *
 * Listens on `pointerdown` rather than `click`: a `click` listener fires after
 * the mouse-up, so a control that unmounts on mousedown never receives it, and
 * text selections that end outside the panel spuriously close it.
 */
export function useDismiss({ open, onDismiss, refs, closeOnEscape = true }: DismissOptions) {
  useEffect(() => {
    if (!open) return;

    const isInside = (target: Node) => refs.some((ref) => ref.current?.contains(target));

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (target && !isInside(target)) onDismiss();
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (closeOnEscape && event.key === 'Escape') {
        event.stopPropagation();
        onDismiss();
      }
    };

    document.addEventListener('pointerdown', handlePointerDown, true);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, true);
      document.removeEventListener('keydown', handleKeyDown);
    };
    // `refs` is a fresh array each render; depending on it would re-bind the
    // listeners constantly. The ref objects inside it are stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, onDismiss, closeOnEscape]);
}
